import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listCourses } from './instructorApi';
import type { Course } from './instructorApi';
import { getActiveChallenge } from '../leaderboard/leaderboardApi';
import type { ActiveChallenge } from '../leaderboard/leaderboardApi';

export const INSTRUCTOR_COURSE_STORAGE_KEY = 'shellgame_instructor_course_id';

function findCourseId(courses: Course[], courseId: string | null | undefined): string | null {
  return courses.find((course) => course.id === courseId)?.id ?? null;
}

function resolveCourseId(
  courses: Course[],
  preferredCourseId: string | null,
  activeChallenge: ActiveChallenge | undefined,
  activeChallengeIsLoading: boolean,
): string | null {
  const preferred = findCourseId(courses, preferredCourseId);
  if (preferred) {
    return preferred;
  }
  if (activeChallengeIsLoading) {
    return null;
  }
  const active = activeChallenge?.active ? findCourseId(courses, activeChallenge.courseId) : null;
  return active ?? courses[0]?.id ?? null;
}

function courseQueryError(
  hasError: boolean,
  error: unknown,
  isSuccess: boolean,
  courses: Course[],
): string | null {
  if (hasError) {
    return error instanceof Error ? error.message : 'Impossible de charger les cours.';
  }
  if (isSuccess && courses.length === 0) {
    return 'Aucun cours trouvé.';
  }
  return null;
}

const selectionListeners = new Set<() => void>();

function subscribeToSelection(listener: () => void): () => void {
  selectionListeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    selectionListeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

function readSelection(): string | null {
  return localStorage.getItem(INSTRUCTOR_COURSE_STORAGE_KEY);
}

/** Persists the selected class and notifies every mounted consumer (menu, selector, pages). */
export function storeInstructorCourseId(courseId: string): void {
  localStorage.setItem(INSTRUCTOR_COURSE_STORAGE_KEY, courseId);
  selectionListeners.forEach((listener) => listener());
}

/**
 * Resolves the instructor's selected class, validating a stored choice against their courses.
 * With no valid choice, an active challenge's class wins, followed by their first class.
 */
export function useInstructorCourseId(enabled = true) {
  const preferredCourseId = useSyncExternalStore(subscribeToSelection, readSelection);
  const coursesQuery = useQuery({
    queryKey: ['instructor', 'courses'],
    queryFn: listCourses,
    enabled,
  });
  const activeChallengeQuery = useQuery({
    queryKey: ['nav', 'active-challenge'],
    queryFn: getActiveChallenge,
    enabled,
  });
  const courses = coursesQuery.data ?? [];
  const selectedCourseId = resolveCourseId(
    courses,
    preferredCourseId,
    activeChallengeQuery.data,
    activeChallengeQuery.isLoading,
  );
  const errorMessage = courseQueryError(
    coursesQuery.isError,
    coursesQuery.error,
    coursesQuery.isSuccess,
    courses,
  );

  return {
    courseId: selectedCourseId,
    courses,
    isLoading: coursesQuery.isLoading || activeChallengeQuery.isLoading,
    errorMessage,
    selectCourse: storeInstructorCourseId,
  };
}
